import { useQuery } from "@tanstack/react-query";
import { tournamentsQueryOptions } from "../../../api/tournaments";
import { useSelectedTeam } from "@/context/teamContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/shadcn/tooltip";
import MaterialIcon from "@/components/general/common/MaterialIcon";

interface PlayersHeaderProps {
    selectedTournamentId?: number;
    fallbackTournamentId?: number;
    onTournamentChange?: (id: number | undefined) => void;
    isLoading?: boolean;
}

export function PlayersHeader({
    selectedTournamentId,
    fallbackTournamentId,
    onTournamentChange,
    isLoading = false,
}: PlayersHeaderProps) {
    const { teamId } = useSelectedTeam();
    const { data: tournaments, isLoading: isTournamentsLoading } = useQuery(tournamentsQueryOptions(teamId));

    const effectiveTournamentId = selectedTournamentId ?? fallbackTournamentId;
    const selectedTournament = tournaments?.find((t) => t.id === effectiveTournamentId)?.name;

    const handleTournamentSelect = (tournamentName: string) => {
        const tournament = tournaments?.find((t) => t.name === tournamentName);
        if (tournament) {
            onTournamentChange?.(tournament.id);
        }
    };

    return (
        <TooltipProvider>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                <div>
                    <h1 className="text-4xl font-black text-primary tracking-tight mb-2 font-headline">
                        Active Roster
                    </h1>
                    <p className="text-on-surface-variant font-medium">
                        Performance tracking for the current season's elite squad.
                    </p>
                </div>
                <div id="onboarding-players-filters" className="flex gap-3 items-center">
                    <Select
                        value={selectedTournament || ""}
                        onValueChange={handleTournamentSelect}
                        disabled={isLoading || isTournamentsLoading || !tournaments || tournaments.length === 0}
                    >
                        <SelectTrigger className="w-full md:w-fit bg-surface-container-highest border-0">
                            <div className="flex items-center gap-2">
                                <MaterialIcon name="sort" className="text-primary text-lg" />
                                <SelectValue placeholder="Select tournament" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            {tournaments && tournaments.length > 0 ? (
                                tournaments.map((tournament) =>
                                    tournament.matches.length === 0 ? (
                                        <Tooltip key={tournament.id} delayDuration={0}>
                                            <TooltipTrigger asChild>
                                                <div
                                                    className="flex items-center px-2 py-1.5 text-sm opacity-50 cursor-not-allowed text-on-surface"
                                                    onClick={(e) => e.preventDefault()}
                                                >
                                                    <div className="flex items-baseline gap-2">
                                                        <span>{tournament.name}</span>
                                                        <span className="text-sm text-on-surface-variant">
                                                            {tournament.id}
                                                        </span>
                                                    </div>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="left">
                                                <p>No data for this tournament</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <SelectItem key={tournament.id} value={tournament.name}>
                                            <div className="flex items-baseline gap-2">
                                                <span>{tournament.name}</span>
                                                <span className="text-sm text-on-surface-variant">{tournament.id}</span>
                                            </div>
                                        </SelectItem>
                                    )
                                )
                            ) : (
                                <div className="px-3 py-2 text-on-surface-variant text-sm">
                                    No tournaments available
                                </div>
                            )}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </TooltipProvider>
    );
}
